<template>
  <div class="pf-chart-element" :class="classObj">
    <!-- CALLOUT LAYER -->
    <template v-if="configAnnotations && configAnnotations.callouts">
      <component
        class="pf-annotation-layer__callout-absolute"
        v-for="(call, call_ndx) in configAnnotations.callouts"
        :key="`call_${call_ndx}`"
        :is="calloutComp(call_ndx.split('_')[0])"
        :id="call_ndx"
        v-show="plotWidth >= smBreakpoint"
        :bounds="{ right: plotRight, bottom: plotBottom }"
        :editable="configuration"
        :configuration="configuration"
        :minimize="minAnnotation"
        :band-x="scaleBandDomain"
        :domain-x="xDomain"
        :domain-y="yDomain"
        :range-x="xRange"
        :range-y="yRange"
        :y-axis-width="yAxisWidth"
        :transpose="transpose"
        v-bind="{
          ...layout.plots[0].props,
          ...call.props,
        }"
        @pfmoveevent="updatePosition($event, call_ndx)"
        @click="configuration ? annSelect(call_ndx) : null"
      />
    </template>
    <!-- HOVER FLAG -->
    <!-- TODO this v-if is getting out of hand -->
    <mce-hover-flag
      :x="flagX"
      :y="flagY"
      v-if="
        mouse &&
        hover &&
        layout.hover &&
        (layout.hover.props.items || []).length &&
        !configuration &&
        !preview
      "
      ref="hoverFlag"
      v-show="flagX > 0 && flagY > 0"
    >
      <mce-legend hide-top-border hide-bottom-border>
        <mce-legend-group :title="labelFormatHover(hover.title)">
          <mce-legend-item
            v-for="(item, item_ndx) in hover"
            :key="'item_' + item_ndx"
            :value="formatFlagValue(Object.hasOwn(item.value, 'y') ? item.value.y : item.value)"
            :color="legendItemColor(item, item_ndx)"
            :label="isFloatingBars ? '' : layout.hover.props.items[item_ndx].label"
            v-bind="layout.hover.props.items[item_ndx]"
          />
        </mce-legend-group>
      </mce-legend>
    </mce-hover-flag>
    <!-- LAYOUT CONTAINER -->
    <mce-layout
      v-if="plotHeight && plotWidth && Object.keys(layout).length"
      :key="renderKey"
      :class="classObj"
      :plot-height="plotHeight"
      :plot-width="plotWidth"
      :axis-left-width="yAxis.left?.width || yAxisWidth"
      :legend-right-width="legendRightWidth"
      ref="layout"
    >
      <!-- PLOTS -->
      <template #default>
        <!-- grid lines -->
        <mce-grid
          :height="`${gridStyle}`"
          pointer-events="visible"
          :range-x="xRange"
          :range-y="yRange"
          :domain-x="gridlines && gridlines.x ? xDomainRange : null"
          :domain-y="gridlines && gridlines.y ? yDomainRange : null"
          :tick-quantity-y="rescaleYTicks"
          :tick-quantity-x="rescaleXLin"
          :time-interval="timeInterval"
          v-bind="layout.plots[0].props"
          @click.native="configuration ? clearSelection() : null"
        />
        <!-- annotation: below plot -->
        <mce-group v-if="configAnnotations">
          <!-- TODO: Replace with dynamic component-->
          <pf-highlight
            v-for="(zone, zone_ndx) in configAnnotations.highlights"
            :key="'highlight_' + zone_ndx"
            :configuration="configuration"
            :band-x="xDomain"
            :domain-x="xDomain"
            :domain-y="yDomain"
            :range-x="xRange"
            :range-y="yRange"
            :transpose="transpose"
            :value-format="valueItemFormat"
            :id="zone_ndx"
            v-bind="{
              ...layout.plots[0].props,
              ...zone.props,
            }"
            @pfmoveevent="updatePosition($event, zone_ndx)"
            @click.native="configuration ? annSelect(zone_ndx) : null"
          ></pf-highlight>
        </mce-group>
        <component
          :is="layout.type"
          :configuration="configuration"
          :mouse="mouse"
          :plots="layout.plots"
          :range-x="xRange"
          :range-y="yRange"
          :domain-x="xDomain"
          :domain-y="yDomain"
          :focused="focused"
          :plot-index="plotIndex"
          @override-layout="overrideLayout"
          @mousemove-plot="onHover"
          @pfselectionplot="addSelection($event)"
          @pflineplot="addSelection($event)"
          @pfselectionbar="addSelection($event)"
        />
        <!-- annotation: above plot -->
        <mce-group v-if="configAnnotations">
          <pf-line
            v-for="(line, line_ndx) in configAnnotations.lines"
            :key="'line' + line_ndx"
            :configuration="configuration"
            :band-x="xDomain"
            :domain-x="xDomain"
            :domain-y="yDomain"
            :range-x="xRange"
            :range-y="yRange"
            :transpose="transpose"
            :value-format="valueItemFormat"
            :id="line_ndx"
            v-bind="{
              ...layout.plots[0].props,
              ...line.props,
            }"
            @pfmoveevent="updatePosition($event, line_ndx)"
            @click.native="configuration ? annSelect(line_ndx) : null"
          ></pf-line>
        </mce-group>
        <mce-pointer v-model="mouse"></mce-pointer>
      </template>
      <!-- LEGEND RIGHT-->
      <template #legend-right v-if="isRightLegend">
        <pf-line-legend
          v-if="useLineLegend"
          ref="legendRight"
          :shrink="plotWidth <= breakpoint"
          :height="plotHeight"
          :domain-x="xDomain"
          :domain-y="yDomain"
          :range-x="xRange"
          :range-y="yRange"
          :plot-index="plotIndex"
          :configuration="configuration"
          :item-format="valueItemFormat"
          v-bind="layout.plots[1].props"
          :legend-props="legendItemPropItems"
          @pflegendupdate="updateLegend"
          @pffocusdata="setFocused"
          @pflineselect="configuration ? addSelection({ plots: 1, ndx: $event }) : null"
        ></pf-line-legend>
        <mce-legend ref="legendRight" v-else>
          <mce-legend-group>
            <mce-legend-item
              v-for="(props, props_ndx) in legendItemPropItems"
              :ref="'legend_item_' + props_ndx"
              :key="'legend_item_' + props_ndx"
              :value="null"
              v-bind="props"
              :class="configClass"
              @click.native="
                configuration
                  ? addSelection({ id: 'legend_item_' + props_ndx, plots: 1, ndx: props_ndx })
                  : null
              "
            />
          </mce-legend-group>
        </mce-legend>
      </template>
      <!-- LEGEND TOP-->
      <template #legend-top v-if="layout.legend && topLegend">
        <mce-legend :horizontal="true" ref="legendTop" :style="`margin-left:${-yAxisWidth}px;`">
          <mce-legend-group>
            <mce-legend-item
              v-for="(props, props_ndx) in layout.legend.props.items"
              :key="'legend_item_' + props_ndx"
              :ref="'legend_item_' + props_ndx"
              :value="null"
              v-bind="props"
              :class="configClass"
              @click.native="
                configuration
                  ? addSelection({ id: 'legend_item_' + props_ndx, plots: 1, ndx: props_ndx })
                  : null
              "
            />
          </mce-legend-group>
        </mce-legend>
      </template>
      <!-- Y-AXIS LEFT -->
      <template #axis-left v-if="yAxis.left">
        <mce-y-axis
          class="axis"
          ref="yAxisLeft"
          :range="yRange"
          :domain="yDomainRange"
          :label-format="labelFormatY"
          :tick-quantity="rescaleYTicks"
          v-bind="yAxis.left.props"
          :class="configClass"
          @click.native.stop="
            configuration && yScaleType !== 'band'
              ? addSelection({ id: 'yAxisLeft', key: 'yAxis' })
              : null
          "
        />
      </template>
      <!-- X-AXIS BOTTOM -->
      <template #axis-bottom v-if="layout.xAxis && layout.xAxis.position === 'bottom'">
        <mce-x-axis
          :class="['axis', configClass, axisClass]"
          ref="xAxisBottom"
          :domain="xDomainRange"
          :range="xRange"
          v-bind="layout.xAxis.props"
          :time-interval="timeInterval"
          :label-format="labelFormatX"
          :tick-quantity="rescaleXLin"
          @click.native="
            configuration && xScaleType !== 'band'
              ? addSelection({ id: 'xAxisBottom', key: 'xAxis' })
              : null
          "
        />
      </template>
    </mce-layout>
  </div>
</template>

<script>
  import MceGrid from '@mce/grid';
  import MceGroup from '@mce/group';
  import MceHoverFlag from '@mce/hover-flag';
  import MceLayout from '@mce/layout';
  import { MceLegend, MceLegendGroup, MceLegendItem } from '@mce/legend';
  import McePointer from '@mce/pointer';
  import MceXAxis from '@mce/x-axis';
  import MceYAxis from '@mce/y-axis';
  import { nice, range } from 'd3-array';
  import { scaleTime } from 'd3-scale';
  import { getIntervalByWidth, localeFormat, timeMap, inlineYear } from '@/utils/timeFormatters';
  import displayTextWidth from '@/utils/textCalcs';
  import Toolbars from '@/config/toolbars';
  import formatValue from '@/utils/valueFormatter';
  import PfHighlight from '../annotations/Highlight';
  import PfLine from '../annotations/Line';
  import PfCalloutText from '../annotations/CalloutText';
  import PfCalloutBubble from '../annotations/CalloutBubble';
  import PfLineLegend from '../plots/LineLegend';

  export default {
    name: 'ChartElements',
    props: {
      annotationsOff: {
        type: Boolean,
        default: false,
      },
      configuration: Boolean,
      selectedAnnotation: Object,
      selectedPlot: Object,
      height: Number,
      layout: Object,
      clicks: Number,
      preview: {
        type: Boolean,
        default: false,
      },
      width: Number,
    },
    components: {
      MceGrid,
      MceGroup,
      MceHoverFlag,
      MceLayout,
      MceLegend,
      MceLegendGroup,
      MceLegendItem,
      McePointer,
      MceXAxis,
      MceYAxis,
      PfHighlight,
      PfLine,
      PfCalloutText,
      PfCalloutBubble,
      PfLineLegend,
    },
    computed: {
      xScaleType() {
        return this.layout.plots ? this.layout.plots[0].props.scaleTypeX : null;
      },
      yScaleType() {
        return this.layout.plots ? this.layout.plots[0].props.scaleTypeY : null;
      },
      labelFormatHover() {
        if (this.xScaleType === 'time') {
          return inlineYear(localeFormat.date);
        }
        return Array.isArray(this.layout.domain.x) ? this.labelFormatX : this.labelFormatY;
      },
      labelFormatX: {
        cache: false,
        get() {
          if (this.xScaleType === 'time') {
            return inlineYear(this.layout.xAxis.props.labelFormat);
          }

          if (this.xScaleType === 'band') {
            return (currentLabel, ndx, total) => this.xDomain[ndx];
          }

          return this.layout.xAxis.props.labelFormat;
        },
      },
      labelFormatY: {
        cache: false,
        get() {
          if (this.yScaleType === 'band') {
            return (currentLabel, ndx, total) => this.yDomain[ndx];
          }

          return this.yAxis.left.props.labelFormat;
        },
      },
      timeInterval: {
        cache: false,
        get() {
          return this.layout.xAxis
            ? this.xScaleType === 'time'
              ? timeMap[this.layout.xAxis.props.timeInterval].apply(null, [
                  this.responsiveXInterval,
                ])
              : this.layout.xAxis.props.timeInterval
            : 'year';
        },
      },
      configAnnotations() {
        return this.annotationsOff ? false : this.layout.annotations;
      },
      classObj() {
        const obj = {};
        obj['pf-configuration__mode'] = this.configuration;
        obj['pf-configuration__hide-axes'] = this.hideAxes;
        obj['pf-chart-element__small'] = this.plotWidth <= this.mobBreakpoint;

        return obj;
      },
      configClass() {
        const obj = {};
        obj['pf-configuration__select'] = this.configuration;

        return obj;
      },
      axisClass() {
        const obj = {};
        obj['pf-x-axis__end'] =
          this.layout && this.layout.xAxis && this.layout.xAxis.props.showEndTicks;

        return obj;
      },
      gridlines() {
        return this.layout.plots[0].gridlines;
      },
      gridStyle() {
        return this.configuration ? this.plotHeight : null;
      },
      hideAxes() {
        return this.configuration && this.selectedAnnotation;
      },
      isFloatingBars() {
        return this.layout.type === 'FloatingVerticalBar';
      },
      legendRightWidth() {
        return 120;
      },
      legendItemPropItems() {
        return this.layout && this.layout.legend ? this.layout.legend.props.items : null;
      },
      valueItemFormat() {
        if (this.layout && this.layout.yAxis && this.layout.yAxis[0].props?.labelDefault) {
          return this.layout.yAxis[0].props?.labelDefault;
        }
        return this.layout && this.layout.xAxis ? this.layout.xAxis.props?.labelDefault : null;
      },
      marginLeft() {
        return (
          (this.yAxis.left ? this.$refs.yAxisLeft.$el.getBoundingClientRect().width + 12 : 0) +
          (this.yAxis.leftSecondary
            ? this.$refs.yAxisLeftSecondary.$el.getBoundingClientRect().width + 12
            : 0)
        );
      },
      marginTop() {
        return (
          (this.layout.xAxis.position === 'bottom'
            ? this.$refs.xAxisBottom.$el.getBoundingClientRect().height
            : 0) -
          (this.layout.xAxis.position === 'top'
            ? this.$refs.xAxisTop.$el.getBoundingClientRect().height
            : 0)
        );
      },
      minAnnotation() {
        return this.plotWidth <= this.smBreakpoint;
      },
      mixedPlots() {
        return this.layout.plots.length > 2;
      },
      plotBottom() {
        return this.$refs.layout
          ? this.$refs.layout.$el.getBoundingClientRect().bottom -
              this.$refs.layout.$el.getBoundingClientRect().top
          : this.plotHeight;
      },
      plotHeight() {
        const { shrink, height } = this;
        const diff = shrink.y >= height || this.configuration ? 0 : shrink.y;
        return height - diff;
      },
      plotWidth() {
        const { shrink, width } = this;
        return width - shrink.x;
      },
      plotRight() {
        return (
          this.plotWidth + (this.$refs.legendRight ? this.legendRightWidth : 0) + this.yAxisWidth
        );
      },
      topLegend() {
        const { type } = this.layout;
        return (
          this.width <= this.breakpoint ||
          this.plotHeight <= this.mobHeight ||
          type.toLowerCase().includes('horizontal')
        );
      },
      rescaleXLin() {
        const defTicks = this.layout.xAxis
          ? this.layout.xAxis.props.tickQuantity
          : this.layout.plots[0].props.tickQuantityX;
        const userDefinedTicks = defTicks || 5;
        const newQuantity = this.plotWidth <= this.mobBreakpoint ? 2 : 3;
        return this.plotWidth >= this.smBreakpoint
          ? userDefinedTicks
          : Math.min(newQuantity, userDefinedTicks);
      },
      rescaleYTicks() {
        const defTicks = this.layout.yAxis
          ? this.layout.yAxis[0].props.tickQuantity
          : this.layout.plots[0].props.tickQuantityY;
        const userDefinedTicks = defTicks || 5;
        const newQuantity = this.plotWidth <= this.mobBreakpoint ? 2 : 3;
        return this.plotWidth >= this.mobBreakpoint
          ? userDefinedTicks
          : Math.min(newQuantity, userDefinedTicks);
      },
      activeBreakpoint() {
        // Using the breakpoints I found in this file for now.
        return this.plotWidth <= this.mobBreakpoint
          ? 'mobile'
          : this.plotWidth <= this.smBreakpoint
          ? 'small'
          : this.plotWidth <= this.breakpoint
          ? 'medium'
          : 'large';
      },
      renderKey() {
        return this.overrides ? Object.keys(this.overrides).length : 0;
      },
      transpose() {
        const { type } = this.layout;
        return type.toLowerCase().includes('horizontal');
      },
      isRightLegend() {
        return this.layout.legend && !this.topLegend;
      },
      useLineLegend() {
        return (
          this.layout.type === 'TimeSeriesLine' &&
          !this.topLegend &&
          !this.legendTooTall &&
          !this.mount
        );
      },
      legendTooTall() {
        let tooTall = false;

        if (
          this.$refs.legendRight &&
          this.$refs.legendRight.$refs &&
          this.$refs.legendRight.$refs.container
        ) {
          const { container } = this.$refs.legendRight.$refs;
          const ndx = container.childNodes.length - 1;
          const lastChild = container.childNodes[ndx];
          const { offsetTop, offsetHeight } = lastChild;

          tooTall = offsetTop + offsetHeight >= this.plotHeight;
        }
        return tooTall;
      },
      xRange() {
        return [0, this.plotWidth];
      },
      yRange() {
        const isHorizontalPlot = ['HorizontalGroupedBar', 'HorizontalStackedBar'].includes(
          this.layout.type
        );
        return isHorizontalPlot ? [0, this.plotHeight] : [this.plotHeight, 0];
      },
      scaleBandDomain() {
        return [...Array(this.layout.domain.x.length).keys()];
      },
      xDomain() {
        return this.niceDomain(this.layout.domain.x, 'x');
      },
      xDomainRange() {
        if (this.xScaleType === 'band') {
          return range(this.xDomain.length);
        }

        return this.xDomain;
      },
      yDomain() {
        return this.niceDomain(this.layout.domain.y[0], 'y');
      },
      yDomainRange() {
        if (this.yScaleType === 'band') {
          return range(this.yDomain.length);
        }

        return this.yDomain;
      },
      yAxis() {
        const [first] = this.layout.yAxis || [];

        return {
          left: first?.position === 'left' && first,
        };
      },
      yAxisWidth() {
        const axisLabelWidth =
          this.yAxis.left &&
          this.yAxis.left.props &&
          this.yAxis.left.props.labels &&
          this.yAxis.left.props.labels.axis
            ? 22
            : 0;
        const axisWidth = this.layout && this.yScaleType === 'band' ? 150 : 86;
        const width = this.yWidth ?? axisWidth;

        return width + axisLabelWidth;
      },
    },
    data() {
      return {
        allowUpdate: false,
        axisObject: [],
        responsiveXInterval: 1,
        breakpoint: 600,
        editable: [],
        focused: null,
        hover: null,
        mouse: null,
        mount: true,
        shrink: { x: 0, y: 0 },
        flagX: 0,
        flagY: 0,
        plotIndex: null,
        smBreakpoint: 500,
        mobBreakpoint: 390,
        mobHeight: 248.92,
        annType: {
          lines: ['line'],
          highlights: ['highlight'],
          callouts: ['text', 'bubble'],
        },
        selectedElement: [],
        newLabels: {},
        widthResize: false,
        yWidth: null,
      };
    },
    methods: {
      addPasteListeners() {
        document.querySelectorAll('[contenteditable]').forEach((item) => {
          item.addEventListener('paste', (event) => {
            event.preventDefault();

            const paste = (event.clipboardData || window.clipboardData).getData('text');
            const selection = window.getSelection();

            if (!selection.rangeCount) {
              return;
            }

            selection.deleteFromDocument();
            selection.getRangeAt(0).insertNode(document.createTextNode(paste));
            selection.collapseToEnd();
            // event.target.innerText = event.clipboardData.getData('text/plain');
          });
        });
      },
      niceDomain(domain, axis) {
        const ticks = axis === 'x' ? this.rescaleXLin : this.rescaleYTicks;

        if (
          (axis === 'y' && this.xScaleType === 'band') ||
          (axis !== 'y' && this.xScaleType === 'linear')
        ) {
          const extent = Array.isArray(domain) ? domain[1] - domain[0] : domain.max - domain.min;
          const padding = axis !== 'x' && this.layout.domain.yPadding ? extent * 0.03 : 0;
          return Array.isArray(domain)
            ? nice(domain[0], domain[1] + padding, ticks)
            : nice(domain.min, domain.max + padding, ticks);
        }

        return Array.isArray(domain) ? domain : nice(domain.min, domain.max, ticks);
      },
      setAxisDefaults(part) {
        // TODO this should run in edit-view where the config is updated with
        // new label default settings. the configuration for the axis label
        // unit, and related updated to the legend and hover-flag should be
        // applied before publishing.
        const {
          layout: { legend, hover, xAxis, yAxis },
        } = this;

        const elements = this.layout.type === 'TimeSeriesLine' ? [hover, legend] : [hover];

        const isY = part === 'yAxis';

        const config = isY ? yAxis : xAxis;

        if (
          config &&
          ((isY && this.yScaleType === 'linear') || (!isY && this.xScaleType === 'linear'))
        ) {
          ((init) => (Array.isArray(config) ? config.map(init) : init(config)))((axis) => {
            const {
              apply: { type, ISO },
            } = axis.props.labelDefault;
            // update the axis label unit
            this.$set(axis.props, 'labels', {
              ...(axis.props.labels || {}),
              unit: type === 'currency' ? ISO : '',
            });

            elements
              .reduce((collection, next) => {
                if (next?.props?.items?.length) {
                  collection.push(...next.props.items);
                }
                return collection;
              }, [])
              .forEach((item) => this.$set(item, 'value', axis.props.labelFormat(item.value)));
          });

          if (
            this.configuration &&
            this.selectedElement.length &&
            this.selectedElement[0].includes(part)
          ) {
            this.setToolbarDefaults(
              isY && !this.selectedAnnotation
                ? { id: 'yAxisLeft', key: 'yAxis' }
                : { id: 'xAxisBottom', key: 'xAxis' }
            );
          }
        }

        this.$nextTick(() => this.checkYAxisWidth());
      },
      checkYAxisCollision() {
        const { yAxisLeft } = this.$refs;

        if (!yAxisLeft || this.yScaleType !== 'linear') {
          return;
        }

        const axis =
          yAxisLeft.$el.childNodes.length > 1
            ? yAxisLeft.$el.childNodes[1]
            : yAxisLeft.$el.childNodes[0];
        const axisHeight = axis.getBoundingClientRect().height;
        const ticks = axis.childNodes;
        if (ticks.length > 1) {
          const secondBottom = ticks[1];
          const firstTop = ticks[ticks.length - 1];
          const secondTop = ticks[ticks.length - 2];
          // Hide second bottom tick
          const { height } = secondBottom.getBoundingClientRect();
          const { top } = getComputedStyle(secondBottom);
          const hideNextTick = parseInt(top, 10) + height >= axisHeight - 20;

          ticks[1].style.opacity = hideNextTick ? 0 : 1;

          // Hide second top tick
          if (
            secondTop.getBoundingClientRect().top - 20 <=
            firstTop.getBoundingClientRect().bottom
          ) {
            secondTop.style.opacity = 0;
          }

          this.widthResize = false;
        }
      },
      checkYAxisWidth() {
        if (this.$refs.yAxisLeft) {
          // get tick values and their widths
          const all = this.$refs.yAxisLeft.ticks.reduce((widths, tick) => {
            const width = displayTextWidth(
              tick.value,
              '16pt MorningstarIntrinsic, HelveticaNeue, "Helvetica Neue", Helvetica, Arial, sans-serif'
            );
            widths.push(width);

            return widths;
          }, []);

          // set axis width to max width plus buffer
          this.yWidth = Math.max(...all) + 20;
        }
      },
      clearSelection() {
        this.selectedElement = [];
        this.deselectElements();
        this.$emit('pfdeselectannotation');
        this.plotIndex = '';
        this.$emit('pfcleartoolbar');
      },
      findFitInterval(interval, limit) {
        // parallel scale
        const scale = scaleTime().domain(this.xDomain).range(this.xRange);

        const i = this.layout.xAxis.props.timeInterval === 'hours' ? interval + 2 : interval;
        // duplicate formatter
        const formatter = timeMap[this.layout.xAxis.props.timeInterval].apply(null, [interval]);

        // get ticks
        const getTicks = (f) => scale.ticks(f);
        const ticks = getTicks(formatter);

        // if ticks are less than limit than return otherwise call again with an increased interval
        if (ticks.length <= limit) {
          return i;
        } else {
          return this.findFitInterval(i + 1, limit);
        }
      },
      rescaleXTicks() {
        // This could be a computed prop, but leaving as a method in case we want to add
        // different rules per scale type.
        const xScale = this.layout.xAxis ? this.layout.xAxis.props.scaleType : false;

        if (!xScale || xScale !== 'time') {
          return null;
        }

        const tickSpaces = getIntervalByWidth(this.plotWidth, this.layout.xAxis.props.labelFormat);
        this.responsiveXInterval = this.findFitInterval(1, tickSpaces);
      },
      formatFlagValue(val) {
        return formatValue(val, this.valueItemFormat, true);
      },
      legendItemColor(item, itemNdx) {
        if (this.isFloatingBars) {
          const { x } = item.value;
          const ndx = parseFloat(x.split('_')[1]);
          return this.layout.plots[1].props.color[ndx];
        }

        return this.layout.hover.props.items[itemNdx].color;
      },
      calloutComp(id) {
        return id.includes('bubble') ? PfCalloutBubble : PfCalloutText;
      },
      onHover(hover) {
        if (this.$refs.hoverFlag) {
          const { hoverFlag, layout } = this.$refs;
          const { height, width } = hoverFlag.$el.getBoundingClientRect();
          const mainHeight = layout.$el.getBoundingClientRect().height;
          const mouseArrow = { x: 4, y: height / 3 };

          const flipX = this.mouse.offsetX > width ? -width : 0;
          const flipY = this.mouse.offsetY > mainHeight / 2 ? height : 0;

          if (!this.layout.type.includes('Bar')) {
            mouseArrow.y = 0;
          }

          this.flagX = this.mouse.offsetX + this.marginLeft + mouseArrow.x + flipX;
          this.flagY = this.mouse.offsetY + mouseArrow.y - flipY;
        }
        this.hover = hover;
      },
      updatePosition(args, id) {
        const ann = id.split('_')[0];
        const [type = null] = Object.keys(this.annType).filter((s) =>
          this.annType[s].includes(ann)
        );
        const obj = ['annotations', type, id, 'props'];
        const { prop, value } = args;
        const key = prop;

        this.$emit('pfupdateconfig', {
          obj,
          key,
          value,
        });
      },
      setFloatingDefaults(args) {
        // bar args -- plots, ndx
        // legend args -- id, plots, ndx
        const { id, ndx, plots } = args;
        const toolbarOpts = Toolbars.FloatingVerticalBar;
        let config = { ...this.layout.plots[plots], configNdx: plots };
        let toolbar = toolbarOpts.plots;

        // legend selection should update selected color
        if (id) {
          config = {
            ...this.layout.legend,
            index: ndx,
            plot: this.layout.plots[1].props,
          };
          toolbar = toolbarOpts.legend;

          // maked selected color match color of index item
          toolbar.options.forEach((option) => {
            const { property, props } = option;
            const { index } = config;
            if (property === 'color') {
              props.selected = config.props.items[index][property];
            }
          });
        } else {
          config.key = 'plots'; // for toolbar component
          const colorOption = toolbar.options[0];
          const { property, props } = colorOption;
          if (property === 'color') {
            // get legend color options
            const colorOptions = [];
            const legendColors = this.layout.legend.props.items.reduce((_, item) => {
              colorOptions.push(item.color);
              return colorOptions;
            }, []);
            props.inputs = legendColors;
            props.selected = config.props.color[ndx];
          }
        }

        // bar selection should update selected color, inputs should be legend item colors
        config.index = parseInt(ndx, 10);
        config.toolbar = toolbar;
        this.$emit('pfloadtoolbar', config);
      },
      setToolbarDefaults(options) {
        const { type } = this.layout;
        const { key, index } = options;
        const keyType = key ?? options;
        const config = isNaN(keyType)
          ? this.layout[keyType][0] ?? this.layout[keyType]
          : this.layout.plots[keyType];
        const toolbar = Toolbars[type];
        let toolbarConfig = isNaN(keyType) ? toolbar[keyType] : toolbar.plots;

        // For VerticalBarLine the options arg is an index of which toolbar to load
        if (type === 'VerticalBarLine' && Number.isInteger(options)) {
          toolbarConfig = toolbar.plots[options - 1];
        }

        if (toolbarConfig && toolbarConfig.options) {
          toolbarConfig.options.forEach((option, ndx) => {
            const { element } = option;
            const prop = option.property;
            const def = option.default;
            const value = prop ? config.props[prop] : this.layout[element];

            if (!value) {
              return;
            }

            const layout = element ? value[def.key][def.index] : value;
            if (typeof def === 'object' && !Array.isArray(def)) {
              if (def.value) {
                const val = layout[def.value];
                option.props[def.attr] = val;
              }
              // get prop values from config bind to options
              if (def.bind) {
                Object.keys(def.bind).forEach((level) => {
                  if (typeof def.bind[level] === 'object' && layout[level]) {
                    Object.keys(def.bind[level]).forEach((apply) => {
                      const propVal = layout[level][apply];
                      option.props[def.bind[level][apply]] = propVal;
                    });
                  } else {
                    const propVal = layout[level];
                    option.props[def.bind[level]] = propVal;
                  }
                });
              }
            } else {
              option.props[def] = Array.isArray(value) ? value[this.plotIndex] : value;
            }
            toolbarConfig.options[ndx] = option;
          });

          if (keyType === 'legend') {
            // maked selected color match color of index item
            toolbarConfig.options.forEach((option) => {
              const { property, props } = option;
              const ndx = index ?? config.index;

              if (property === 'color') {
                props.selected = config.props.items[ndx][property];
              }
            });

            config.plot = this.layout.plots[1].props;
          }
        }

        config.index = this.plotIndex ?? options.index;
        config.key = isNaN(keyType) ? keyType : 'plots';
        config.configNdx = isNaN(keyType) ? config.index : keyType;

        const obj = {
          ...config,
          toolbar: toolbarConfig,
        };
        this.$emit('pfloadtoolbar', obj);
      },
      annSelect(id) {
        this.plotIndex = '';
        this.deselectElements();
        this.$emit('pfannotationselected', id);
      },
      deselectElements() {
        const vm = this;
        Object.values(this.$refs).forEach((ref) => {
          if (ref) {
            if (ref[0]) {
              ref[0].$el.classList.remove('pf-selected-elem');
            } else if (ref.$el) {
              if (ref.$el.children.length > 0) {
                // TODO: discuss this AirBnB rule with the team and decide if for/of loops are bad
                // eslint-disable-next-line no-restricted-syntax
                for (const child of ref.$el.children) {
                  child.classList.remove('pf-selected-elem');
                }
              }
              ref.$el.classList.remove('pf-selected-elem');
            }
          }
        });

        document.removeEventListener('keydown', (evt) => {
          if (evt.keyCode === 27) {
            vm.clearSelection();
          }
        });
      },
      addSelection(args) {
        const { id, key, ndx, plots } = args;
        const ref = id ?? `legend_item_${ndx}`;
        const configNdx = this.mixedPlots ? ndx + 1 : plots; // If there are different plot types in config the index will be one more than legend position
        const options = key ?? configNdx; // get toolbar options for config element, if plot pass index (number)

        if (ndx != null) {
          this.plotIndex = parseInt(ndx, 10);
        } else {
          this.plotIndex = '';
        }

        if (this.isFloatingBars && (!id || !id.toLowerCase().includes('axis'))) {
          this.setFloatingDefaults(args);
        } else {
          this.setToolbarDefaults(options);
        }

        if (id || !this.isFloatingBars) {
          this.selectedElement = [ref];
        }
      },
      overrideLayout(options) {
        this.overrides = options;
      },
      overrideAxesLabels(axis) {
        const isX = axis === 'x';
        const ndx = isX ? 1 : 0;
        const newClass = 'pf-axis-label';
        const { xAxisBottom, yAxisLeft } = this.$refs;
        const parent = isX ? xAxisBottom : yAxisLeft;
        const label = parent.$el.childNodes[ndx];
        const span =
          label.childNodes[0] && label.childNodes[0].tagName === 'SPAN'
            ? label.childNodes[0]
            : label;

        span.contentEditable = true;
        if (label.classList) {
          label.classList.add(newClass);
        }

        // y label seems to be lagging on edit
        if (!isX) {
          span.addEventListener('paste', (event) => {
            event.preventDefault();
            event.target.innerText = event.clipboardData.getData('text/plain');
          });
        }

        span.addEventListener('input', this.doUpdate, true);

        span.addEventListener('blur', this.updateAxisLabel, true);

        span.addEventListener('focus', this.updateClick, true);

        span.addEventListener('keydown', (evt) => {
          if (evt.keyCode === 13 || evt.keyCode === 27) {
            evt.target.blur();
          }
        });
      },
      overrideLegendLabels() {
        const { legendTop, legendRight } = this.$refs;
        const parent = legendRight ?? legendTop;

        if (!parent.$children[0]) {
          return;
        }

        const items = parent.$children[0].$children;

        items.forEach((item, ndx) => {
          const span = item.$el.lastElementChild;
          span.contentEditable = true;
          span.classList.add('pf-tick-edit');
          span.addEventListener('input', (ev) => this.doUpdate(ev), true);
          span.addEventListener('blur', (ev) => this.updateLegend({ $event: ev, ndx }), true);

          span.addEventListener('keydown', (evt) => {
            if (evt.keyCode === 13 || evt.keyCode === 27) {
              evt.target.blur();
            }
          });
        });
      },
      updateAxisLabel(ev) {
        if (!this.allowUpdate) {
          return;
        }
        const { target } = ev;
        const isX = target.className.includes('axis-x');
        const value = target.textContent.trim();

        this.axisObject = isX ? ['xAxis', 'props', 'labels'] : ['yAxis', 0, 'props', 'labels'];
        this.labelClick += 1;

        const obj = {
          obj: this.axisObject,
          key: 'axis',
          value,
        };

        this.$emit('pfupdateconfig', obj);
        this.allowUpdate = false;
      },
      doUpdate() {
        this.allowUpdate = true;
      },
      overrideTickLabels(axis) {
        const { xAxisBottom, yAxisLeft } = this.$refs;
        const parent = axis === 'x' ? xAxisBottom : yAxisLeft;
        const ticks = parent.$el.children[0].childNodes;

        ticks.forEach((span, ndx) => {
          span.contentEditable = true;
          span.classList.add('pf-tick-edit');
          span.addEventListener('input', (ev) => this.doUpdate(ev), true);
          span.addEventListener('blur', (ev) => this.updateLabel(ev, axis, ndx), true);

          span.addEventListener('keydown', (evt) => {
            if (evt.keyCode === 13 || evt.keyCode === 27) {
              evt.target.blur();
            }
          });
        });
      },
      calcShrink() {
        requestAnimationFrame(() => {
          const { layout, legendRight, legendTop, xAxisBottom, yAxisLeft } = this.$refs;

          const spacing = layout?.spacing || 0;

          // TODO: @Graham how do layout resize plot on switch to legend top
          this.shrink.x =
            (legendRight && !this.topLegend ? legendRight.$el.offsetWidth + spacing : 0) +
            (yAxisLeft ? yAxisLeft.$el.offsetWidth + spacing : 0);

          this.shrink.y = (legendTop?.$el.offsetHeight || 0) + (xAxisBottom?.$el.offsetHeight || 0);
        });
      },
      setFocused(data) {
        this.focused = data;
      },
      updateLabel(e, axis, ndx) {
        if (!this.allowUpdate) {
          return;
        }

        const { innerText } = e.target;
        this.newLabels[ndx] = innerText.trim();
        const data = axis === 'x' ? this.xDomain : this.yDomain;

        const newDomain = data.map((label, cNdx) => this.newLabels[cNdx] ?? label);
        const newObj =
          axis === 'x'
            ? {
                obj: ['domain'],
                key: 'x',
                value: newDomain,
              }
            : {
                obj: ['domain', 'y'],
                key: 0,
                value: newDomain,
              };

        this.$emit('pfupdateconfig', newObj);
        this.allowUpdate = false;
      },
      updateLegend(args) {
        if (!this.allowUpdate && !args.allowUpdate) {
          return;
        }

        const { innerText } = args.$event.target;

        // legend > props > items > ndx > label
        // hover > props > items > ndx > label
        const obj = ['props', 'items', args.ndx];
        const key = 'label';
        const value = innerText.trim();

        this.$emit('pfupdateconfig', { obj: ['legend', ...obj], key, value });
        this.$emit('pfupdateconfig', { obj: ['hover', ...obj], key, value });
        this.allowUpdate = false;
      },
      adjustEndTick() {
        const { xAxisBottom } = this.$refs;
        const ticks = xAxisBottom.$el.children[0].children;
        let totalWidth = 0;
        Array.from(ticks).forEach((tick) => {
          const { width, marginLeft } = getComputedStyle(tick);

          totalWidth += parseFloat(width) + parseFloat(marginLeft);

          if (this.plotWidth - totalWidth < 0) {
            tick.style.display = 'none';
          }
        });
      },
      adjustAxisShowTick() {
        const vm = this;

        if (this.layout.type === 'TimeSeriesLine' && this.layout.xAxis) {
          // hide ticks in time series that exceed plotWidth instead of wrapping them
          this.$nextTick(() => {
            setTimeout(() => {
              vm.adjustEndTick();
            }, 100);
          });

          if (this.layout.xAxis.props.showEndTicks) {
            // MCE divides last two tick widths to fit, PF adds line break for year to wrap needing less space
            this.$nextTick(() => {
              setTimeout(() => {
                vm.updateTickWidthEnd();
              }, 100);
            });
          }
        }
      },
      updateOverrideLabels() {
        const vm = this;

        if (this.configuration) {
          this.overrideAxesLabels('y');
          this.overrideAxesLabels('x');
        }

        if (this.configuration && !this.useLineLegend) {
          this.overrideLegendLabels();
        }

        if (this.configuration && this.xScaleType === 'band') {
          // make x-ticks contenteditable
          this.overrideTickLabels('x');
        }

        if (this.configuration && this.yScaleType === 'band') {
          // make y-ticks contenteditable
          this.$nextTick(() => {
            setTimeout(() => {
              vm.overrideTickLabels('y');
            }, 100);
          });
        }

        if (this.configuration && this.mount) {
          this.$nextTick(() => {
            setTimeout(() => {
              vm.addPasteListeners();
            }, 100);
          });
        }

        this.mount = false;
      },
      updateTickWidthEnd() {
        const { xAxisBottom } = this.$refs;
        const ticks = xAxisBottom.$el.children[0].children;
        Array.from(ticks).forEach((tick, ndx) => {
          const timeInt = this.layout.xAxis.props.timeInterval;
          const isSmall = this.plotWidth <= this.mobBreakpoint;
          const totalTicks = isSmall ? 7 : 10;
          // float last tick right
          // hide second to last tick
          if (ndx === ticks.length - 1) {
            tick.style.display = 'inline-block';
            tick.style.marginLeft = '0px';

            if (
              ndx - 1 === ticks.length - 2 &&
              ticks.length >= totalTicks &&
              timeInt === 'months'
            ) {
              const tickWidth = ticks[ndx - 2].getBoundingClientRect().width;
              const lastTick = ticks[ndx].getBoundingClientRect().left;
              const secondLastTick = ticks[ndx - 1].getBoundingClientRect().right;
              ticks[ndx - 1].style.width = tickWidth;

              // hide second to last tick if right overlaps by 6 or more
              ticks[ndx - 1].style.opacity = lastTick < secondLastTick - 6 ? 0 : 1;
            }
          } else {
            ticks[ndx].style.opacity = 1;
          }
        });
      },
    },
    mounted() {
      this.setAxisDefaults('yAxis');
      this.setAxisDefaults('xAxis');
      this.calcShrink();
      this.updateOverrideLabels();
      this.adjustAxisShowTick();
    },
    watch: {
      activeBreakpoint() {
        this.rescaleXTicks();
        this.adjustAxisShowTick();
      },
      clicks: {
        handler() {
          this.clearSelection();
        },
      },
      layout: {
        deep: true,
        handler() {
          this.rescaleXTicks();
          this.calcShrink();
          this.adjustAxisShowTick();
          this.updateOverrideLabels();
        },
      },
      width: {
        deep: true,
        handler() {
          this.widthResize = true;
          this.calcShrink();
          this.checkYAxisCollision();
        },
      },
      height: {
        deep: true,
        handler() {
          if (!this.widthResize) {
            this.checkYAxisCollision();
          }
        },
      },
      yAxis: {
        deep: true,
        handler() {
          this.setAxisDefaults('yAxis');
          this.setAxisDefaults('xAxis');
        },
      },
      yDomain() {
        this.checkYAxisCollision();
      },
      plotWidth() {
        const vm = this;
        this.rescaleXTicks();
        this.$nextTick(() => {
          setTimeout(() => {
            vm.checkYAxisCollision();
          }, 40);
        });
        this.updateOverrideLabels();
      },
      selectedAnnotation: {
        deep: true,
        handler(val) {
          if (val) {
            this.deselectElements();
            this.plotIndex = '';
          }
        },
      },
      selectedPlot: {
        deep: true,
        handler(val) {
          if (!val) {
            this.clearSelection();
          }
        },
      },
      selectedElement() {
        const vm = this;
        this.deselectElements();
        this.$emit('pfdeselectannotation');

        this.selectedElement.forEach((name) => {
          if (name) {
            const ref = vm.$refs[name];
            if (ref != null) {
              if (ref[0]) {
                ref[0].$el.classList.add('pf-selected-elem');
              } else if (ref.$el) {
                ref.$el.classList.add('pf-selected-elem');
              }
            }
          }
        });

        document.addEventListener('keydown', (evt) => {
          if (evt.code === 'Escape') {
            vm.clearSelection();
          }
        });
      },
      topLegend() {
        this.calcShrink();
      },
    },
    beforeDestroy() {
      if (this.configuration) {
        document.querySelectorAll('[contenteditable]').forEach((item) => {
          item.removeEventListener('paste', (event) => {
            event.preventDefault();
            event.target.innerText = event.clipboardData.getData('text/plain');
          });
        });
      }
    },
  };
</script>

<style lang="scss">
  @import '@mds/constants';

  .pf-chart-element {
    position: relative;
    overflow: hidden;
  }

  .pf-hover-flag {
    opacity: 0;

    &.pf-show--flag {
      opacity: 1;
    }
  }

  .pf-selected-elem {
    transition: border-width 200ms ease-in-out;
    box-shadow: $mds-box-shadow-focus;
  }

  .axis {
    text-wrap: nowrap;

    span {
      word-break: break-word;
    }
  }

  .pf-tick-edit {
    &:hover {
      background: rgba($mds-interactive-color-primary-default, 0.25);
      border-radius: 5px;
    }
  }

  .pf-configuration {
    &__select {
      cursor: pointer;
      border: 2px solid transparent;
      margin: -2px;

      &:hover {
        border: 2px solid rgba($mds-interactive-color-primary-default, 1);
        border-radius: 5px;
      }
    }
  }

  .pf-annotation-layer__callout-absolute {
    position: absolute;
    top: 0;
    left: 0;

    .pf-configuration__mode & {
      cursor: grab;
    }
  }

  .mce-grid__x-tick___PF:last-child {
    opacity: 1;
  }
</style>

<style module lang="scss">
  // MDS overrides only
  @import '@mds/constants';
  @import '@mds/typography';

  :global(.pf-chart-element) {
    .mce-axis-y__tick,
    .mce-axis-x__tick,
    .mce-hover-flag,
    .mce-legend__item-label {
      @include mds-body-text-m();
    }

    .mce-axis-x__ticks-time {
      .mce-axis-x__tick {
        white-space: pre;
        text-indent: -4px;
        text-align: left;
      }
    }
  }

  :global(.pf-x-axis__end) {
    .mce-axis-x__tick:last-child {
      text-align: right;
    }
  }

  :global(.pf-configuration__mode) {
    overflow: unset;

    .mce-legend--horizontal {
      padding-top: 6px;
    }

    .mce-legend {
      overflow-x: clip;
    }
  }

  :global(.pf-configuration__hide-axes) {
    .mce-layout__axis-bottom,
    .mce-bar-plot-horizontal,
    .mce-bar-plot-vertical-grouped,
    .mce-bar-plot-vertical,
    .mce-bar-plot-vertical-stacked,
    .mce-line-plot {
      transition: opacity 0.3s;
      pointer-events: none;
      opacity: 0.3;
    }

    .mce-axis-y,
    .mce-axis-x {
      opacity: 0.8;
      pointer-events: none;
    }
  }

  :global(.pf-configuration__select) {
    &.mce-axis-y,
    &.mce-axis-x {
      box-sizing: content-box;
    }

    .mce-legend__item-label:hover {
      background: rgba($mds-interactive-color-primary-default, 0.25);
      border-radius: 5px;
    }
  }
</style>
